import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useParams } from "react-router-dom";
import AIInsights from "./pages/AIInsights";
import { DataProvider } from "./context/DataContext";

const Metrics = lazy(() => import("./pages/Metrics"));
const Home = lazy(() => import("./pages/Home"));
const Portfolios = lazy(() => import("./pages/Portfolios"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ProtectedRoute = lazy(() => import("./components/shared/ProtectedRoute"));
const Layout = lazy(() => import("./components/shared/Layout"));
const Login = lazy(() => import("./pages/Login"));

const Transactions = lazy(() => import("./pages/Transactions"));
const Goal = lazy(() => import("./pages/Goal"));
const Performance = lazy(() => import("./pages/Performance"));
const Allocation = lazy(() => import("./pages/Allocation"));
const TaxReport = lazy(() => import("./pages/TaxReport"));

/** Legacy flat routes predate multi-portfolio support and now bounce back to the picker. */
const LEGACY_PATHS = ["/transactions", "/goal", "/performance", "/allocation", "/metrics", "/insights", "/tax"];

const PortfolioDataLayout = () => {
	const { portfolioId } = useParams<{ portfolioId: string }>();

	if (!portfolioId) {
		return <Navigate to="/" replace />;
	}

	return (
		<DataProvider key={portfolioId} portfolioId={portfolioId}>
			<Outlet />
		</DataProvider>
	);
};

function App() {
	return (
		<BrowserRouter>
			<Suspense
				fallback={
					<div className="theme-grid-bg flex h-screen w-screen items-center justify-center text-zinc-500 dark:text-zinc-400">
						Loading...
					</div>
				}
			>
				<Routes>
					<Route path="/login" element={<Login />} />

					<Route element={<ProtectedRoute />}>
						<Route path="/" element={<Portfolios />} />
						<Route path="/settings" element={<Settings />} />

						{LEGACY_PATHS.map((path) => (
							<Route key={path} path={path} element={<Navigate to="/" replace />} />
						))}

						<Route path="/portfolios/:portfolioId" element={<PortfolioDataLayout />}>
							<Route element={<Layout />}>
								<Route index element={<Home />} />
								<Route path="transactions" element={<Transactions />} />
								<Route path="goal" element={<Goal />} />
								<Route path="performance" element={<Performance />} />
								<Route path="allocation" element={<Allocation />} />
								<Route path="metrics" element={<Metrics />} />
								<Route path="insights" element={<AIInsights />} />
								<Route path="tax" element={<TaxReport />} />
							</Route>
						</Route>
					</Route>

					<Route path="*" element={<NotFound />} />
				</Routes>
			</Suspense>
		</BrowserRouter>
	);
}

export default App;
